import { useState } from 'react'
import AddressInput from '../AddressInput'
import {
  Grid,
  GridItem,
  Spinner,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Select,
} from '@chakra-ui/react'
import { SEARCH_RADIUS_IN_METERS } from '../../../../utils/constants'
import SuggestedCorrespondents from './components/SuggestedCorrespondents'
import { useGetUsersAroundPoint, UserAround } from '../../../../api/api_hooks/user/useGetUsersAroundPoint'
import MapView from './components/MapView'
import { useTranslation } from 'react-i18next'
import { useLoadScript } from '@react-google-maps/api'

const libraries: ("places")[] = ['places']

interface SearchUsersComponentProps {
  selectedUser: UserAround | null
  setSelectedUser: (user: UserAround | null) => void
}

const SearchUsersComponent = ({
  selectedUser,
  setSelectedUser,
}: SearchUsersComponentProps) => {
  const { t } = useTranslation()
  const radiusOptions = [5, 10, 25, 50]
  const [searchParams, setSearchParams] = useState({
    location: { lat: '', lon: '' },
    radius: SEARCH_RADIUS_IN_METERS,
  })
  const {
    data: users,
    isLoading,
    error,
  } = useGetUsersAroundPoint({
    latitude: Number(searchParams.location.lat),
    longitude: Number(searchParams.location.lon),
    radius: searchParams.radius,
  })

  // Use useLoadScript hook instead of LoadScript component to prevent duplicate script loads
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: libraries,
    preventGoogleFontsLoading: true, // Prevent loading Google fonts
  })

  return (
    <Box w="full">
      {/* Header + Controls */}
      <VStack align="stretch" spacing={5} mb={6}>
        <Box>
          <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={1}>
            {t('sharedDiagnostic.searchByLocation') || 'Rechercher par localisation'}
          </Text>
          {users && users.length > 0 && (
            <Text fontSize="sm" color="gray.600">
              {users.length} expert{users.length > 1 ? 's' : ''} trouvé{users.length > 1 ? 's' : ''}
            </Text>
          )}
        </Box>

        <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={4}>
        <GridItem minW={0}>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {t('shared.common.cityOrAdressLabel')}
            </Text>
            <AddressInput
                onLocationSelect={(location) => setSearchParams({ ...searchParams, location })}
                label=""
                fullAddressOnly={false}
              />
            </VStack>
          </GridItem>
        <GridItem minW={0}>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              Rayon de recherche
            </Text>
            <Select
                value={searchParams.radius / 1000}
                onChange={(e) =>
                  setSearchParams({
                    ...searchParams,
                    radius: Number(e.target.value) * 1000,
                  })
                }
                size="md"
                bg="white"
              >
                {radiusOptions.map((km) => (
                  <option key={km} value={km}>
                    {km} km
                  </option>
                ))}
              </Select>
            </VStack>
          </GridItem>
        </Grid>
      </VStack>

      {/* Two Column Layout: Map (Left 50%) + Expert List (Right 50%) */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} w="full">
        {/* Left column: Google Map */}
        <GridItem minW={0}>
          <Box
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            overflow="hidden"
            h="500px"
            display="flex"
            flexDirection="column"
          >
            <Box px={4} py={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                {t('sharedDiagnostic.mapView') || 'Carte des experts'}
              </Text>
            </Box>
            <Box flex={1} position="relative">
              {loadError ? (
                <Alert status="error" h="100%" borderRadius={0}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Erreur de chargement</AlertTitle>
                    <AlertDescription fontSize="xs">{loadError.message}</AlertDescription>
                  </Box>
                </Alert>
              ) : !isLoaded ? (
                <VStack spacing={3} py={10}>
                  <Spinner size="lg" color="purple.500" />
                  <Text color="gray.600" fontSize="sm">
                    {t('shared.common.loading')} Google Maps...
                  </Text>
                </VStack>
              ) : (
                <MapView searchParams={searchParams} users={users ?? []} selectedUser={selectedUser} />
              )}
            </Box>
          </Box>
        </GridItem>

        {/* Right column: Expert List */}
        <GridItem minW={0}>
          <Box
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            overflow="hidden"
            h="500px"
            display="flex"
            flexDirection="column"
          >
            <Box px={4} py={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                {t('sharedDiagnostic.availableExperts') || 'Experts disponibles'}
              </Text>
              {selectedUser && (
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Sélectionné: {selectedUser.contactName}
                </Text>
              )}
            </Box>

            <Box
              flex={1}
              overflowY="auto"
              overflowX="visible"
              px={4}
              py={4}
              position="relative"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'gray.50',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'gray.300',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'gray.400',
                },
              }}
            >
              {isLoading && (
                <VStack spacing={3} py={10}>
                  <Spinner size="lg" color="purple.500" />
                  <Text color="gray.600" fontSize="sm">
                    {t('shared.common.loading')}...
                  </Text>
                </VStack>
              )}
              {error && (
                <Alert status="error" borderRadius="lg">
                  <AlertIcon />
                  <Text fontSize="sm">{t('shared.errors.errorMsg')}</Text>
                </Alert>
              )}
              {!isLoading && !error && (
                <SuggestedCorrespondents
                  selectedUser={selectedUser}
                  users={users ?? []}
                  onSelectUser={setSelectedUser}
                />
              )}
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  )
}

export default SearchUsersComponent
